/**
 * WinTheDayHero — REMODELED 2026-06-13 to land on Five Below brand
 * energy ("Let Go & Have Fun"). The previous solid amber/brown
 * "Let's Rally" state read as corporate/old to the user. Now every
 * state lives on a vibrant on-brand gradient with a confetti dot
 * pattern + a soft decorative blob in the corner.
 *
 * Palette
 *   Winning        → emerald → teal       (celebration green, fresh)
 *   Needs Push     → Five Below blue → indigo → purple (on-brand drive)
 *   Critical Focus → rose → pink → fuchsia (urgent but playful, NOT brown)
 *   Let's Rally    → violet → fuchsia → pink (the FB "fun" gradient)
 *
 * The hero uses gradients on rich, saturated bases — not muddy darks —
 * so the gradient adds depth without losing the brand pop.
 */
import { useState } from "react";
import { Lightning, Trophy, Sparkle, ArrowsClockwise, Target } from "@phosphor-icons/react";

function pickStatus({ captureRate, goal, transactions, atGoal }) {
  const ratePct = (captureRate || 0) * 100;
  const goalPct = (goal || 0.4) * 100;
  if (atGoal || ratePct >= goalPct) {
    return {
      key: "winning",
      label: "Winning",
      tagline: "Goal hit. Keep the energy up.",
      gradient: "linear-gradient(135deg, #10b981 0%, #14b8a6 55%, #06b6d4 100%)",
      blob: "rgba(255,255,255,0.18)",
      Icon: Trophy,
    };
  }
  if (ratePct >= goalPct * 0.85) {
    return {
      key: "needs_push",
      label: "Needs Push",
      tagline: "Close — one strong shift gets us there.",
      gradient: "linear-gradient(135deg, #0066FF 0%, #4f46e5 50%, #7c3aed 100%)",
      blob: "rgba(255,255,255,0.18)",
      Icon: Lightning,
    };
  }
  if ((transactions || 0) > 0 && ratePct < goalPct * 0.6) {
    return {
      key: "critical",
      label: "Critical Focus",
      tagline: "Big gap — every email matters right now.",
      gradient: "linear-gradient(135deg, #f43f5e 0%, #ec4899 55%, #d946ef 100%)",
      blob: "rgba(255,255,255,0.22)",
      Icon: Sparkle,
    };
  }
  return {
    key: "rally",
    label: "Let's Rally",
    tagline: "Game's on. Time to make the push.",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #d946ef 55%, #ec4899 100%)",
    blob: "rgba(255,255,255,0.22)",
    Icon: ArrowsClockwise,
  };
}

function storeLabel(storeId, settings) {
  const raw = (settings?.store_name || "").trim();
  if (raw) return raw.replace(/^store[_\s]/i, "");
  return String(storeId || "").replace(/^store_/, "") || "—";
}

export default function WinTheDayHero({
  tallyData,
  wtdData,
  mtdData,
  settings,
  storeId,
  todayFocus,
  dateLabel,
}) {
  // Today / WTD / MTD pill toggle (Wave A.5 + A.6 2026-06-13). All
  // three data sources are passed in pre-fetched — no new poll, no
  // new endpoint call from inside the hero. MTD uses
  // `/api/mtd-summary` which mirrors `wtd-summary`'s field names so
  // the display branch is uniform.
  const [range, setRange] = useState("today");
  const periodSource = range === "wtd" ? wtdData : range === "mtd" ? mtdData : null;
  const isPeriod = !!periodSource;
  const captureRate = tallyData?.capture_rate || 0;
  const transactions = tallyData?.store_transactions || 0;
  const captured = tallyData?.total_emails || 0;
  const goal = settings?.capture_goal || 0.4;
  const atGoal = tallyData?.at_goal || false;
  const status = pickStatus({ captureRate, goal, transactions, atGoal });
  const Icon = status.Icon;
  const goalPct = Math.round((goal || 0) * 100);
  const displayRatePct = isPeriod
    ? Math.round((periodSource.week_capture_rate || 0) * 1000) / 10
    : Math.round((captureRate || 0) * 1000) / 10;
  const displayCaptured = isPeriod ? (periodSource.week_emails || 0) : captured;
  const displayTx = isPeriod ? (periodSource.week_transactions || 0) : transactions;
  const periodGoalPct = isPeriod ? Math.round((periodSource.goal_rate || 0) * 100) : goalPct;
  const periodLabel = range === "wtd" ? "WTD" : range === "mtd" ? "MTD" : "";
  const periodEmailsSub = range === "wtd" ? "emails this week" : range === "mtd" ? "emails this month" : "emails today";
  const periodTxSub = range === "wtd" ? "this week" : range === "mtd" ? "this month" : "so far today";

  return (
    <section
      data-testid="win-the-day-hero"
      data-status={status.key}
      className="relative overflow-hidden rounded-3xl shadow-xl text-white ring-1 ring-white/20"
      style={{
        background: status.gradient,
      }}
    >
      {/* Decorative confetti dots — adds Five Below playfulness without
          competing with the type. Stays under content via z-0. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.18] pointer-events-none mix-blend-screen"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.85) 1.5px, transparent 1.6px)",
          backgroundSize: "20px 20px",
        }}
      />
      {/* Decorative blob (top right) */}
      <div
        aria-hidden
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-2xl pointer-events-none"
        style={{ background: status.blob }}
      />
      {/* Decorative blob (bottom left) */}
      <div
        aria-hidden
        className="absolute -bottom-20 -left-10 w-48 h-48 rounded-full blur-3xl pointer-events-none"
        style={{ background: status.blob }}
      />

      <div className="relative px-5 sm:px-6 py-4 sm:py-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-black tracking-[0.22em] uppercase opacity-80">
              {dateLabel}
            </p>
            <h1
              className="text-2xl sm:text-3xl font-black leading-[0.95] mt-1 drop-shadow-[0_1px_2px_rgba(0,0,0,0.15)]"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
              data-testid="hero-title"
            >
              Win The Day
            </h1>
            <p className="text-[11px] sm:text-xs font-bold opacity-90 mt-0.5 truncate">
              Store {storeLabel(storeId, settings)}
            </p>
          </div>
          <div
            className="shrink-0 flex flex-col items-end gap-1"
            data-testid="hero-status-chip"
          >
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm ring-1 ring-white/30 text-[10px] sm:text-[11px] font-black tracking-wider uppercase shadow-sm">
              <Icon weight="fill" className="w-3 h-3" />
              {status.label}
            </span>
          </div>
        </div>

        {/* Today / WTD toggle (Wave A.5). Single pill switcher above
            the 3 big numbers. Toggle ONLY swaps the numbers below —
            status chip, tagline, and focus pill stay TODAY-scoped
            (the hero answers "are we winning RIGHT NOW?"). MTD
            deferred until aggregation is built. */}
        {(wtdData || mtdData) && (
          <div className="mt-3 flex items-center gap-1 self-start rounded-full bg-white/15 backdrop-blur-sm ring-1 ring-white/25 p-0.5 w-fit" role="tablist" data-testid="hero-range-toggle">
            <button
              type="button"
              onClick={() => setRange("today")}
              className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all ${
                range === "today" ? "bg-white text-slate-900 shadow-sm" : "text-white/80 hover:text-white"
              }`}
              data-testid="hero-range-today"
            >
              Today
            </button>
            {wtdData && (
              <button
                type="button"
                onClick={() => setRange("wtd")}
                className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all ${
                  range === "wtd" ? "bg-white text-slate-900 shadow-sm" : "text-white/80 hover:text-white"
                }`}
                data-testid="hero-range-wtd"
              >
                WTD
              </button>
            )}
            {mtdData && (
              <button
                type="button"
                onClick={() => setRange("mtd")}
                className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all ${
                  range === "mtd" ? "bg-white text-slate-900 shadow-sm" : "text-white/80 hover:text-white"
                }`}
                data-testid="hero-range-mtd"
              >
                MTD
              </button>
            )}
          </div>
        )}

        {/* Big numbers — the SINGLE most important glance lives here.
            Detail (per-checkpoint breakdown) is the GoalCalculator below. */}
        <div className="mt-2.5 grid grid-cols-3 gap-2 sm:gap-3">
          <div className="rounded-xl bg-white/10 backdrop-blur-[2px] ring-1 ring-white/20 px-2.5 py-2">
            <p className="text-[9px] uppercase tracking-wider opacity-80 font-black">
              Capture Rate
            </p>
            <p
              className="text-2xl sm:text-3xl font-black tabular-nums leading-none mt-0.5"
              data-testid="hero-capture-rate"
            >
              {displayRatePct.toFixed(1)}%
            </p>
            <p className="text-[9px] opacity-80 font-semibold mt-0.5">
              {isPeriod ? `${periodLabel} · vs goal ${periodGoalPct}%` : `vs goal ${goalPct}%`}
            </p>
          </div>
          <div className="rounded-xl bg-white/10 backdrop-blur-[2px] ring-1 ring-white/20 px-2.5 py-2">
            <p className="text-[9px] uppercase tracking-wider opacity-80 font-black">
              Captured
            </p>
            <p
              className="text-2xl sm:text-3xl font-black tabular-nums leading-none mt-0.5"
              data-testid="hero-captured"
            >
              {displayCaptured}
            </p>
            <p className="text-[9px] opacity-80 font-semibold mt-0.5">
              {periodEmailsSub}
            </p>
          </div>
          <div className="rounded-xl bg-white/10 backdrop-blur-[2px] ring-1 ring-white/20 px-2.5 py-2">
            <p className="text-[9px] uppercase tracking-wider opacity-80 font-black">
              Transactions
            </p>
            <p
              className="text-2xl sm:text-3xl font-black tabular-nums leading-none mt-0.5"
              data-testid="hero-transactions"
            >
              {displayTx}
            </p>
            <p className="text-[9px] opacity-80 font-semibold mt-0.5">
              {periodTxSub}
            </p>
          </div>
        </div>

        {/* Today's focus — pulled from existing zone_sheet.store_focus
            field; rendered as a fun pill instead of a slab so the
            hero stays scannable. */}
        {todayFocus && (
          <div
            className="mt-3 flex items-start gap-2 rounded-xl bg-white/95 text-slate-900 px-3 py-2 shadow-md"
            data-testid="hero-focus-line"
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 shadow-sm" style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)" }}>
              <Target weight="fill" className="w-3 h-3 text-white" />
            </span>
            <div className="min-w-0">
              <p className="text-[9px] font-black uppercase tracking-wider text-slate-500 leading-none">
                Today&apos;s Focus
              </p>
              <p className="text-xs font-black leading-snug truncate mt-0.5">
                {todayFocus}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
